import type { TickerSuggestion } from "@/shared/lib/types";

/** Canonical listing facts required by session-relative market signals. */
export interface WatchCanonicalListingIdentity {
  mic_code: string;
  asset_type: "COMMON_STOCK" | "ETF";
  country_code: "US";
}

const MIC_CODE_PATTERN = /^[A-Z0-9]{4}$/;

function canonicalAssetType(value: unknown): WatchCanonicalListingIdentity["asset_type"] | null {
  if (typeof value !== "string") return null;
  const assetType = value.trim().toUpperCase();
  if (assetType === "CS" || assetType === "COMMON_STOCK") return "COMMON_STOCK";
  return assetType === "ETF" ? "ETF" : null;
}

/**
 * Convert one authenticated ticker-search selection to its canonical listing.
 *
 * A typed symbol is deliberately insufficient: percentage references depend
 * on a specific active U.S. stock listing and its exchange session calendar.
 */
export function canonicalListingIdentityFromSuggestion(
  suggestion: TickerSuggestion,
): WatchCanonicalListingIdentity | null {
  if (suggestion.listing_identity !== undefined) {
    const identity = suggestion.listing_identity;
    const assetType = canonicalAssetType(identity.asset_type);
    if (!assetType) return null;
    return normalizeWatchCanonicalListingIdentity({
      mic_code: identity.mic_code.trim().toUpperCase(),
      asset_type: assetType,
      country_code: identity.country_code.trim().toUpperCase() as "US",
    });
  }

  // Compatibility only for older authenticated lookup responses. New
  // responses must use listing_identity; provider-shaped fields are display
  // metadata and never override a canonical identity supplied by the server.
  const micCode = typeof suggestion.primary_exchange === "string"
    ? suggestion.primary_exchange.trim().toUpperCase()
    : "";
  const assetType = canonicalAssetType(suggestion.type);
  const locale = typeof suggestion.locale === "string"
    ? suggestion.locale.trim().toLowerCase()
    : "";
  const market = typeof suggestion.market === "string"
    ? suggestion.market.trim().toLowerCase()
    : "";

  if (suggestion.active !== true
    || locale !== "us"
    || market !== "stocks"
    || !MIC_CODE_PATTERN.test(micCode)
    || !assetType) {
    return null;
  }

  return {
    mic_code: micCode,
    asset_type: assetType,
    country_code: "US",
  };
}

/** Revalidate stored listing facts at the payload boundary. */
export function normalizeWatchCanonicalListingIdentity(
  identity: WatchCanonicalListingIdentity | null,
): WatchCanonicalListingIdentity | null {
  if (!identity
    || identity.country_code !== "US"
    || !MIC_CODE_PATTERN.test(identity.mic_code)
    || canonicalAssetType(identity.asset_type) !== identity.asset_type) {
    return null;
  }

  return {
    mic_code: identity.mic_code,
    asset_type: identity.asset_type,
    country_code: "US",
  };
}
